import React, { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import {
  Box,
  Paper,
  Typography,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Autocomplete,
  TextField,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import SnackbarWrapper from "components/Snackbar";
import ConfirmationDialog from "components/ConfirmationDialog";
import LoadingScreen from "components/Loading";
import { useAuth } from "context/authContext";
import {
  processPayrollPeriod,
  closePayrollPeriod,
  lockPayrollPeriod,
  getPayrollRunsByPeriod,
  createPayrollRun,
  deletePayrollRun,
  calculatePayrollRun,
  populateRunEmployees,
} from "services/api";

const periodStatusColors = {
  open: "success",
  processing: "info",
  closed: "warning",
  locked: "default",
};

const runStatusColors = {
  draft: "default",
  calculated: "info",
  approved: "secondary",
  posted: "warning",
  paid: "success",
};

const runTypeValues = ["regular", "off_cycle", "correction", "bonus_run"];

const PeriodDashboardView = ({ selectedPeriod, onPeriodUpdated, onSelectRun, canEdit }) => {
  const { t } = useTranslation();
  const { employee } = useAuth();
  const snackbarRef = useRef();

  const [period, setPeriod] = useState(selectedPeriod);
  const [runs, setRuns] = useState([]);
  const [loadingRuns, setLoadingRuns] = useState(false);

  const [createRunOpen, setCreateRunOpen] = useState(false);
  const [runType, setRunType] = useState("regular");

  // Generic confirm dialog reused for every destructive/irreversible action
  // on this page (period transitions + deleting a draft run).
  const [pendingAction, setPendingAction] = useState(null); // { type, run? }

  useEffect(() => {
    setPeriod(selectedPeriod);
  }, [selectedPeriod]);

  useEffect(() => {
    const loadRuns = async () => {
      if (!period) return;
      setLoadingRuns(true);
      const res = await getPayrollRunsByPeriod(period.id);
      setRuns(res !== "error" && Array.isArray(res) ? res : []);
      setLoadingRuns(false);
    };
    loadRuns();
  }, [period?.id]);

  const updatePeriodLocally = (updated) => {
    setPeriod(updated);
    onPeriodUpdated?.(updated);
  };

  const handleProcess = async () => {
    const res = await processPayrollPeriod(period.id);
    if (res !== "error") {
      updatePeriodLocally(res);
      snackbarRef.current?.showSnackbar(
        t("admin.payroll.periodsAndRuns.dashboard.processSuccess"),
        "success"
      );
    } else {
      snackbarRef.current?.showSnackbar(
        t("admin.payroll.periodsAndRuns.dashboard.processError"),
        "error"
      );
    }
    setPendingAction(null);
  };

  const handleClose = async () => {
    const res = await closePayrollPeriod(period.id);
    if (res !== "error") {
      updatePeriodLocally(res);
      snackbarRef.current?.showSnackbar(
        t("admin.payroll.periodsAndRuns.dashboard.closeSuccess"),
        "success"
      );
    } else {
      snackbarRef.current?.showSnackbar(
        t("admin.payroll.periodsAndRuns.dashboard.closeError"),
        "error"
      );
    }
    setPendingAction(null);
  };

  const handleLock = async () => {
    const res = await lockPayrollPeriod(period.id);
    if (res !== "error") {
      updatePeriodLocally(res);
      snackbarRef.current?.showSnackbar(
        t("admin.payroll.periodsAndRuns.dashboard.lockSuccess"),
        "success"
      );
    } else {
      snackbarRef.current?.showSnackbar(
        t("admin.payroll.periodsAndRuns.dashboard.lockError"),
        "error"
      );
    }
    setPendingAction(null);
  };

  const handleCreateRun = async () => {
    const res = await createPayrollRun({
      payrollPeriodId: period.id,
      runType,
      initiatedUID: employee?.id,
    });
    if (res !== "error") {
      setRuns([...runs, res]);
      // A run with nobody attached calculates "successfully" but produces
      // zero results and zero errors - always populate right after create
      // so that never happens silently.
      const populateRes = await populateRunEmployees(res.id);
      if (populateRes !== "error") {
        snackbarRef.current?.showSnackbar(
          t("admin.payroll.periodsAndRuns.dashboard.createRunSuccess"),
          "success"
        );
      } else {
        snackbarRef.current?.showSnackbar(
          t("admin.payroll.periodsAndRuns.dashboard.populateAfterCreateError"),
          "error"
        );
      }
      setCreateRunOpen(false);
      setRunType("regular");
    } else {
      snackbarRef.current?.showSnackbar(
        t("admin.payroll.periodsAndRuns.dashboard.createRunError"),
        "error"
      );
    }
  };

  const handleCalculate = async (run) => {
    const res = await calculatePayrollRun(run.id);
    if (res !== "error") {
      setRuns(runs.map((r) => (r.id === res.id ? res : r)));
      snackbarRef.current?.showSnackbar(
        t("admin.payroll.periodsAndRuns.dashboard.calculateSuccess"),
        "success"
      );
    } else {
      snackbarRef.current?.showSnackbar(
        t("admin.payroll.periodsAndRuns.dashboard.calculateError"),
        "error"
      );
    }
  };

  const handleDeleteRun = async () => {
    const res = await deletePayrollRun(pendingAction.run.id);
    if (res === "success") {
      setRuns(runs.filter((r) => r.id !== pendingAction.run.id));
      snackbarRef.current?.showSnackbar(
        t("admin.payroll.periodsAndRuns.dashboard.deleteRunSuccess"),
        "success"
      );
    } else {
      snackbarRef.current?.showSnackbar(
        t("admin.payroll.periodsAndRuns.dashboard.deleteRunError"),
        "error"
      );
    }
    setPendingAction(null);
  };

  const confirmHandlers = {
    process: handleProcess,
    close: handleClose,
    lock: handleLock,
    deleteRun: handleDeleteRun,
  };

  const confirmMessages = {
    process: t("admin.payroll.periodsAndRuns.dashboard.confirmProcess"),
    close: t("admin.payroll.periodsAndRuns.dashboard.confirmClose"),
    lock: t("admin.payroll.periodsAndRuns.dashboard.confirmLock"),
    deleteRun: t("admin.payroll.periodsAndRuns.dashboard.confirmDeleteRun"),
  };

  if (!period) {
    return (
      <Typography color="text.secondary">
        {t("admin.payroll.periodsAndRuns.noPeriodSelected")}
      </Typography>
    );
  }

  return (
    <Box>
      <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            flexWrap: "wrap",
            gap: 2,
          }}
        >
          <Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
              <Typography variant="h6">{period.name}</Typography>
              <Chip
                size="small"
                label={t(`admin.payroll.periodsAndRuns.statuses.${period.status}`)}
                color={periodStatusColors[period.status] || "default"}
              />
            </Box>
            <MDTypography variant="caption" color="text">
              {period.startDate} – {period.endDate} ·{" "}
              {t("admin.payroll.periodsAndRuns.list.fields.payDate")}: {period.payDate}
            </MDTypography>
          </Box>

          {canEdit && (
            <Box sx={{ display: "flex", gap: 1 }}>
              {period.status === "open" && (
                <MDButton
                  size="small"
                  color="info"
                  onClick={() => setPendingAction({ type: "process" })}
                >
                  {t("admin.payroll.periodsAndRuns.dashboard.process")}
                </MDButton>
              )}
              {(period.status === "open" || period.status === "processing") && (
                <MDButton
                  size="small"
                  color="warning"
                  onClick={() => setPendingAction({ type: "close" })}
                >
                  {t("admin.payroll.periodsAndRuns.dashboard.close")}
                </MDButton>
              )}
              {period.status === "closed" && (
                <MDButton
                  size="small"
                  color="error"
                  onClick={() => setPendingAction({ type: "lock" })}
                >
                  {t("admin.payroll.periodsAndRuns.dashboard.lock")}
                </MDButton>
              )}
            </Box>
          )}
        </Box>
      </Paper>

      <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
        <MDTypography variant="subtitle2" sx={{ mb: 1 }}>
          {t("admin.payroll.periodsAndRuns.dashboard.checklistTitle")}
        </MDTypography>
        <MDTypography variant="body2" color="text">
          {t("admin.payroll.periodsAndRuns.dashboard.step1Inputs")}
        </MDTypography>
        <MDTypography variant="body2" color="text" sx={{ mb: 1 }}>
          {t("admin.payroll.periodsAndRuns.dashboard.step2Runs")}
        </MDTypography>
      </Paper>

      <Paper variant="outlined" sx={{ p: 2 }}>
        <MDBox
          sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}
        >
          <MDTypography variant="subtitle2">
            {t("admin.payroll.periodsAndRuns.dashboard.runsTitle")}
          </MDTypography>
          {canEdit && (
            <MDButton size="small" color="primary" onClick={() => setCreateRunOpen(true)}>
              {t("admin.payroll.periodsAndRuns.dashboard.createRun")}
            </MDButton>
          )}
        </MDBox>

        {loadingRuns ? (
          <LoadingScreen />
        ) : runs.length === 0 ? (
          <MDTypography variant="body2" color="text">
            {t("admin.payroll.periodsAndRuns.dashboard.noRuns")}
          </MDTypography>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableBody>
                <TableRow>
                  <TableCell sx={{ fontWeight: "bold" }}>
                    {t("admin.payroll.periodsAndRuns.dashboard.fields.runType")}
                  </TableCell>
                  <TableCell sx={{ fontWeight: "bold" }}>
                    {t("admin.payroll.periodsAndRuns.dashboard.fields.runDate")}
                  </TableCell>
                  <TableCell sx={{ fontWeight: "bold" }} align="center">
                    {t("admin.payroll.periodsAndRuns.dashboard.fields.status")}
                  </TableCell>
                  <TableCell sx={{ fontWeight: "bold" }} align="right">
                    {t("admin.payroll.periodsAndRuns.dashboard.fields.actions")}
                  </TableCell>
                </TableRow>
                {runs.map((run) => (
                  <TableRow key={run.id} hover>
                    <TableCell>
                      {t(`admin.payroll.periodsAndRuns.dashboard.runTypes.${run.runType}`)}
                    </TableCell>
                    <TableCell>
                      {run.runDate ? new Date(run.runDate).toLocaleString() : "—"}
                    </TableCell>
                    <TableCell align="center">
                      <Chip
                        size="small"
                        label={t(
                          `admin.payroll.periodsAndRuns.dashboard.runStatuses.${run.status}`
                        )}
                        color={runStatusColors[run.status] || "default"}
                      />
                    </TableCell>
                    <TableCell align="right">
                      <Box sx={{ display: "flex", gap: 1, justifyContent: "flex-end" }}>
                        {canEdit && run.status === "draft" && (
                          <MDButton
                            size="small"
                            variant="outlined"
                            color="info"
                            onClick={() => handleCalculate(run)}
                          >
                            {t("admin.payroll.periodsAndRuns.dashboard.calculate")}
                          </MDButton>
                        )}
                        {canEdit && run.status === "draft" && (
                          <MDButton
                            size="small"
                            variant="outlined"
                            color="error"
                            onClick={() => setPendingAction({ type: "deleteRun", run })}
                          >
                            {t("common.delete")}
                          </MDButton>
                        )}
                        <MDButton size="small" onClick={() => onSelectRun?.(run)}>
                          {t("admin.payroll.periodsAndRuns.dashboard.viewRun")}
                        </MDButton>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      <Dialog open={createRunOpen} onClose={() => setCreateRunOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: "bold" }}>
          {t("admin.payroll.periodsAndRuns.dashboard.createRun")}
        </DialogTitle>
        <DialogContent>
          <Autocomplete
            sx={{ mt: 1 }}
            options={runTypeValues}
            getOptionLabel={(value) =>
              t(`admin.payroll.periodsAndRuns.dashboard.runTypes.${value}`)
            }
            value={runType}
            onChange={(event, newValue) => setRunType(newValue || "regular")}
            disableClearable
            renderInput={(params) => (
              <TextField
                {...params}
                label={t("admin.payroll.periodsAndRuns.dashboard.fields.runType")}
              />
            )}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <MDButton onClick={() => setCreateRunOpen(false)} color="secondary">
            {t("common.cancel")}
          </MDButton>
          <MDButton onClick={handleCreateRun} color="primary">
            {t("common.add")}
          </MDButton>
        </DialogActions>
      </Dialog>

      <ConfirmationDialog
        open={!!pendingAction}
        onClose={() => setPendingAction(null)}
        onConfirm={() => confirmHandlers[pendingAction?.type]?.()}
        message={pendingAction ? confirmMessages[pendingAction.type] : ""}
      />

      <SnackbarWrapper
        ref={snackbarRef}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      />
    </Box>
  );
};

PeriodDashboardView.propTypes = {
  selectedPeriod: PropTypes.object,
  onPeriodUpdated: PropTypes.func,
  onSelectRun: PropTypes.func,
  canEdit: PropTypes.bool.isRequired,
};

PeriodDashboardView.defaultProps = {
  selectedPeriod: null,
  onPeriodUpdated: () => {},
  onSelectRun: () => {},
};

export default PeriodDashboardView;
